import { useState } from 'react';
import { Button } from '@appwin/ui';
import { api, ApiError, type AdminRelease } from './api.js';

/** Edits a published version: link, hash, size, silent switches, status. */
export function EditVersionForm({
  release,
  onClose,
  onSaved,
}: {
  release: AdminRelease;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [downloadUrl, setDownloadUrl] = useState(release.downloadUrl ?? '');
  const [sha256, setSha256] = useState(release.sha256);
  const [silent, setSilent] = useState((release.silentArgs ?? []).join(' '));
  const [status, setStatus] = useState<'published' | 'yanked'>(
    release.status === 'published' ? 'published' : 'yanked',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateRelease(release.id, {
        downloadUrl,
        sha256,
        silentArgs: silent.split(/\s+/).filter(Boolean),
        status,
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const valid = /^https:\/\/.+/i.test(downloadUrl) && /^[a-f0-9]{64}$/i.test(sha256);

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold">Версия {release.version}</h2>
        <p className="mt-1 text-xs text-muted">
          {release.kind.toUpperCase()} · {release.scope}
        </p>

        <div className="mt-4 space-y-3">
          <Field label="Ссылка на установщик">
            <input
              value={downloadUrl}
              onChange={(event) => setDownloadUrl(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>

          <Field label="SHA-256">
            <input
              value={sha256}
              onChange={(event) => setSha256(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm"
            />
          </Field>

          <Field label="Параметры тихой установки" hint="Пусто — автообновление недоступно.">
            <input
              value={silent}
              onChange={(event) => setSilent(event.target.value)}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm"
            />
          </Field>

          <Field label="Статус">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as 'published' | 'yanked')}
              disabled={busy}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            >
              <option value="published">опубликована</option>
              <option value="yanked">отозвана</option>
            </select>
          </Field>
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={save} disabled={busy || !valid}>
            {busy ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
